import { Loader2, RefreshCw, Smartphone, X } from 'lucide-react';
import { formatBrazilPhone } from '../../../../../utils/whatsapp';

const WhatsAppQrModal = ({
  open,
  image,
  phoneNumber,
  loading,
  error,
  unavailable = false,
  title = 'Atendimento publicado',
  description,
  closeLabel = 'Fechar e ver conexão do WhatsApp',
  onRetry,
  onClose,
}: {
  open: boolean;
  image?: string | null;
  phoneNumber?: string | null;
  loading?: boolean;
  error?: string | null;
  unavailable?: boolean;
  title?: string;
  description?: string;
  closeLabel?: string;
  onRetry: () => void;
  onClose: () => void;
}) => {
  if (!open) return null;

  const imageSource = image
    ? (image.startsWith('data:image') ? image : `data:image/png;base64,${image}`)
    : '';

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/60 px-4 py-6 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="atendabot-whatsapp-qr-title"
        className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-white px-5 py-4 pr-14">
          <div className="flex items-start gap-3">
            <span className="rounded-xl bg-indigo-600 p-2.5 text-white">
              <Smartphone size={22} />
            </span>
            <div>
              <h2 id="atendabot-whatsapp-qr-title" className="text-lg font-bold text-slate-900">
                {title}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {description || `Agora conecte o WhatsApp ${formatBrazilPhone(phoneNumber) || phoneNumber}.`}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar QR Code"
            className="absolute right-4 top-4 rounded-lg p-2 text-slate-500 transition hover:bg-white hover:text-slate-800"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-5 text-center sm:p-6">
          {loading ? (
            <div className="flex min-h-72 flex-col items-center justify-center gap-3" role="status">
              <Loader2 className="animate-spin text-indigo-600" size={38} />
              <p className="font-semibold text-slate-800">Gerando o QR Code…</p>
              <p className="text-sm text-slate-500">A conexão pode levar alguns segundos para responder.</p>
            </div>
          ) : imageSource ? (
            <>
              <p className="font-semibold text-slate-800">Escaneie o QR Code no WhatsApp</p>
              <p className="mt-1 text-sm text-slate-500">WhatsApp → Aparelhos conectados → Conectar um aparelho.</p>
              <img
                src={imageSource}
                alt="QR Code para conectar o WhatsApp ao AtendaBot"
                className="mx-auto mt-5 h-64 w-64 rounded-xl border border-slate-200 bg-white p-3"
              />
              <button
                type="button"
                onClick={onRetry}
                className="mt-5 inline-flex items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <RefreshCw size={16} /> Gerar novo QR Code
              </button>
            </>
          ) : (
            <div className="space-y-4">
              <p className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800" role="alert">
                {error || 'Não foi possível gerar o QR Code agora.'}
              </p>
              {!unavailable && (
                <button
                  type="button"
                  onClick={onRetry}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-indigo-700"
                >
                  <RefreshCw size={16} /> Tentar gerar novamente
                </button>
              )}
            </div>
          )}

          {!loading && (
            <button
              type="button"
              onClick={onClose}
              className="mt-4 block w-full rounded-lg px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50"
            >
              {closeLabel}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppQrModal;
